import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldAlert, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * AIDiscussionDailyCap — small inline control to set the per-book daily
 * AI-message cap. Rendered inside the AI Discussion AIBotPanel via the
 * `extraControls` prop.
 *
 * Default: 5 messages per book per 24h (prevents manual + auto runs from
 * stacking 6+ AI posts on one book in a day, blowing the LLM budget).
 *
 * Range: 0 (disable cap) … 50. Server validates the same bounds.
 */
const AIDiscussionDailyCap = ({ initialCap, defaultCap, onSaved }) => {
  const [value, setValue] = useState(String(initialCap ?? defaultCap ?? 5));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (initialCap !== undefined && initialCap !== null) {
      // Sync the input when the parent re-fetches and the value changes
      // upstream (e.g. another admin saves a new cap concurrently).
      setValue(String(initialCap));
    }
  }, [initialCap]);

  const dirty = String(initialCap ?? '') !== value;

  const handleSave = async () => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0 || num > 50 || !Number.isInteger(num)) {
      toast({
        title: 'Invalid value',
        description: 'Daily cap must be a whole number between 0 and 50.',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/ai-discussion/daily-cap`,
        { daily_cap: num },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: num === 0 ? 'Daily cap disabled' : `Daily cap set to ${num}/book`,
        description: res.data?.message || 'Saved.',
      });
      if (onSaved) onSaved(num);
    } catch (err) {
      toast({
        title: 'Could not save daily cap',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10"
      data-testid="ai-discussion-daily-cap"
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert className="h-4 w-4 text-amber-400" />
        <span className="text-xs font-semibold text-amber-300 uppercase tracking-wide">
          LLM Budget Guard
        </span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-300 whitespace-nowrap" htmlFor="ai-discussion-daily-cap-input">
          Max per book / 24h
        </label>
        <input
          id="ai-discussion-daily-cap-input"
          type="number"
          min={0}
          max={50}
          step={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-20 bg-zinc-900 border border-white/15 rounded-md px-2 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
          data-testid="ai-discussion-daily-cap-input"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={saving || !dirty}
          onClick={handleSave}
          className="border-white/15 text-white hover:bg-white/10"
          data-testid="ai-discussion-daily-cap-save"
        >
          {saving ? '...' : <><Check className="h-3.5 w-3.5 mr-1" /> Save</>}
        </Button>
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
        Per-book cap on AI messages in any rolling 24h window. Both manual
        and scheduled runs count toward this. Set to <code className="text-gray-300">0</code> to disable.
      </p>
    </div>
  );
};

export default AIDiscussionDailyCap;
